import React, { useState } from "react";
import { Mui, MuiIcons } from "@leap4/frontend";
import * as XLSX from "xlsx";

const { FormHelperText, TextField, Button, Chip, IconButton, Tooltip, Box } = Mui;
const { Upload: UploadIcon, Close: CloseIcon } = MuiIcons;


export interface IGlobalBundleInputProps {
    globalBundleList: string[];
    setBundleList: (bundles: string[]) => void;
    error: string;
    setError: (error: string) => void;
    title: string;
    showExportButton: boolean;
}

const GlobalBundleInput = ({
                               globalBundleList,
                               setBundleList,
                               error,
                               setError,
                               title,
                               showExportButton
                           }: IGlobalBundleInputProps) => {
    const [inputValue, setInputValue] = useState("");

    // helper that processes an input string and applies the same validations
    const processInputChips = (value: string) => {
        if (!value.trim()) return;
        
        // split based on commas or newlines
        const tokens = value.split(/[\n,]+/).map(token => token.trim()).filter(token => token);
        
        // validations
        if (tokens.some(token => token === "")) {
            setError(`Invalid format: check if there is an extra comma or empty value.`);
            return;
        } else if (tokens.some(token => token.includes(" "))) {
            setError("Invalid format: remove extra spaces within entries.");
            return;
        }
        
        // Update the bundle list and clear the input
        setBundleList([...globalBundleList, ...tokens]);
        setInputValue("");
        setError("");
    };

    const handleAddChip = () => {
        processInputChips(inputValue);
    };

    const handleDeleteChip = (chip: string) => {
        setBundleList(globalBundleList.filter((item) => item !== chip));
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const data = event.target?.result;
            if (typeof data === "string") {
                const workbook = XLSX.read(data, { type: "binary" });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const csvData = XLSX.utils.sheet_to_csv(sheet);
                const bundleIds = csvData.split("\n").map((id) => id.trim());
                setBundleList(bundleIds);
            }
        };
        reader.readAsBinaryString(file);
    };

    // paste handler to process copy-pasted text into chips
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData("text");
        processInputChips(pasteData);
    };

    /**
     * Creates and downloads a CSV file from the current `globalBundleList`.
     */
    const handleExport = () => {
        // Create CSV content
        const csvContent = globalBundleList.join("\n");

        // Convert CSV string to Blob
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

        // Create a download link and invoke it
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "bundle_list.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <FormHelperText>{title}</FormHelperText>
            <Box
                display="flex"
                flexDirection="column"
                gap={2}
                border="1px solid #ccc"
                borderRadius={2}
                padding={2}
                minHeight={150}
                maxHeight={300}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <Box
                        flex="1"
                        maxHeight={150}
                        overflow="auto"
                        display="flex"
                        flexWrap="wrap"
                        gap={1}
                    >
                        {globalBundleList?.map((chip) => (
                            <Chip
                                key={chip}
                                label={chip}
                                onDelete={() => handleDeleteChip(chip)}
                                color="default"
                            />
                        ))}
                    </Box>
                   <Tooltip title={"Delete All"}>
                    <IconButton
                        onClick={() => {
                            setBundleList([]);
                            setError("");
                        }}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                     </Tooltip>
                </Box>

                {/* TextField for adding new bundles */}
                <TextField
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddChip();
                        } else if (e.key === "Backspace" && inputValue === "") {
                            handleDeleteChip(globalBundleList[globalBundleList.length - 1]);
                        }
                    }}
                    onPaste={handlePaste}
                    placeholder={globalBundleList.length === 0 ? "Add values (Input values and press Enter, either comma-separated values or one at a time)" : ""}
                    variant="standard"
                    InputProps={{
                        disableUnderline: true,
                    }}
                    style={{
                        background: "transparent",
                    }}
                />
            </Box>
            {error && <FormHelperText error>{error}</FormHelperText>}
            <Box display="flex" justifyContent="flex-start" mt={2} gap={1}>
                <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                    Upload CSV File
                    <input
                        type="file"
                        accept=".csv, .xlsx, .xls"
                        hidden
                        onChange={handleFileUpload}
                    />
                </Button>
                {showExportButton && (<Button variant="outlined" onClick={handleExport}>
                    Export as CSV
                </Button>)
                }
            </Box>
        </>
    );
};

export default GlobalBundleInput;
